import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WarningIcon from '@mui/icons-material/Warning';
import axiosInstance from '../../lib/axios';
import { TableToolbar, ColumnConfig } from '../../components/TableToolbar';
import { getUser } from '../../utils/auth';
import { useAnimationStyles } from '../../hooks/useAnimations';
import type { StockForecast as StockForecastType, LocationStats } from '../../types';

const fetchReorderAlerts = async (locationId?: string) => {
  const params: any = {};
  if (locationId) {
    params.locationId = locationId;
  }
  const response = await axiosInstance.get('/inventory/organization/reorder-alerts', { params });
  return response.data;
};

const fetchLocationStats = async (locationId: string) => {
  const response = await axiosInstance.get(`/inventory/location/${locationId}/stats`);
  return response.data as LocationStats;
};

const fetchLocationForecasts = async (locationId: string) => {
  const response = await axiosInstance.get(`/inventory/location/${locationId}/forecasts`);
  return response.data as StockForecastType[];
};

const getSalesTrendIcon = (trend: 'increasing' | 'decreasing' | 'stable') => {
  switch (trend) {
    case 'increasing':
      return <TrendingUpIcon color="success" fontSize="small" />;
    case 'decreasing':
      return <TrendingDownIcon color="error" fontSize="small" />;
    default:
      return <TrendingFlatIcon color="action" fontSize="small" />;
  }
};

const getSalesTrendColor = (trend: 'increasing' | 'decreasing' | 'stable'): 'success' | 'error' | 'default' => {
  switch (trend) {
    case 'increasing':
      return 'success';
    case 'decreasing':
      return 'error';
    default:
      return 'default';
  }
};

const getStockoutUrgency = (days: number | null): { color: 'error' | 'warning' | 'success' | 'default'; label: string } => {
  if (days === null) {
    return { color: 'default', label: 'N/A' };
  }
  if (days <= 7) {
    return { color: 'error', label: `${days} days` };
  }
  if (days <= 14) {
    return { color: 'warning', label: `${days} days` };
  }
  return { color: 'success', label: `${days} days` };
};

const exportColumns: ColumnConfig[] = [
  { field: 'productName', headerName: 'Product' },
  { field: 'currentStock', headerName: 'Current Stock' },
  { field: 'averageDailySales', headerName: 'Avg Daily Sales' },
  { field: 'daysUntilStockout', headerName: 'Days Until Stockout' },
  { field: 'reorderPoint', headerName: 'Reorder Point' },
  { field: 'recommendedReorderQuantity', headerName: 'Recommended Order' },
  { field: 'salesTrend', headerName: 'Trend' },
];

export const StockForecast = () => {
  const user = getUser();
  const locationId = user?.locationId;
  const animations = useAnimationStyles();

  const [tabValue, setTabValue] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const { data: reorderAlerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['reorder-alerts', locationId],
    queryFn: () => fetchReorderAlerts(locationId),
    enabled: !!user?.organizationId,
  });

  const { data: locationStats, isLoading: statsLoading } = useQuery({
    queryKey: ['location-stats', locationId],
    queryFn: () => fetchLocationStats(locationId!),
    enabled: !!locationId,
  });

  const { data: forecasts, isLoading: forecastsLoading } = useQuery({
    queryKey: ['location-forecasts', locationId],
    queryFn: () => fetchLocationForecasts(locationId!),
    enabled: !!locationId,
  });

  const currentData = tabValue === 0 ? reorderAlerts : forecasts;
  const isLoading = tabValue === 0 ? alertsLoading : forecastsLoading;

  const filteredData = (currentData || []).filter((item: StockForecastType) => {
    if (searchValue) {
      return item.productName.toLowerCase().includes(searchValue.toLowerCase());
    }
    return true;
  });

  const columns: GridColDef[] = [
    {
      field: 'productName',
      headerName: 'Product',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'currentStock',
      headerName: 'Current Stock',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value <= 0 ? 'error' : params.value < 10 ? 'warning' : 'success'}
          size="small"
        />
      ),
    },
    {
      field: 'averageDailySales',
      headerName: 'Avg Daily Sales',
      width: 140,
      valueFormatter: (params: any) => params.value?.toFixed(2) || '0',
    },
    {
      field: 'daysUntilStockout',
      headerName: 'Days to Stockout',
      width: 150,
      renderCell: (params) => {
        const { color, label } = getStockoutUrgency(params.value);
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {params.value !== null && params.value <= 7 && (
              <WarningIcon color="error" fontSize="small" />
            )}
            <Chip label={label} color={color} size="small" />
          </Stack>
        );
      },
    },
    {
      field: 'reorderPoint',
      headerName: 'Reorder Point',
      width: 130,
    },
    {
      field: 'recommendedReorderQuantity',
      headerName: 'Order Qty',
      width: 120,
      renderCell: (params) => (
        <Typography fontWeight={params.value > 0 ? 'bold' : 'normal'} color={params.value > 0 ? 'primary' : 'text.secondary'}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'salesTrend',
      headerName: 'Trend',
      width: 120,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {getSalesTrendIcon(params.value)}
          <Chip
            label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
            color={getSalesTrendColor(params.value)}
            size="small"
            variant="outlined"
          />
        </Stack>
      ),
    },
    {
      field: 'lastSaleDate',
      headerName: 'Last Sale',
      width: 150,
      valueFormatter: (params: any) =>
        params.value ? new Date(params.value).toLocaleDateString() : 'Never',
    },
  ];

  return (
    <Box sx={animations.fadeIn}>
      <Typography variant="h4" mb={3} sx={animations.slideDown}>
        Stock Forecast & Reorder Alerts
      </Typography>

      {/* Stats Summary */}
      {!statsLoading && locationStats && (
        <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
          <Card sx={{ minWidth: 150, ...animations.staggeredCard(0), ...animations.hoverLift }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Products
              </Typography>
              <Typography variant="h5">{locationStats.totalProducts}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 150, ...animations.staggeredCard(1), ...animations.hoverLift }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Stock
              </Typography>
              <Typography variant="h5">{locationStats.totalQuantity}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 150, ...animations.staggeredCard(2), ...animations.hoverLift }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Available
              </Typography>
              <Typography variant="h5" color="success.main">
                {locationStats.totalAvailable}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 150, ...animations.staggeredCard(3), ...animations.hoverLift }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Low Stock
              </Typography>
              <Typography variant="h5" color="warning.main">
                {locationStats.lowStockCount}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 150, ...animations.staggeredCard(4), ...animations.hoverLift }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Out of Stock
              </Typography>
              <Typography variant="h5" color="error.main">
                {locationStats.outOfStockCount}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 150, ...animations.staggeredCard(5), ...animations.hoverLift }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Stock Value
              </Typography>
              <Typography variant="h5">{locationStats.totalValue.toLocaleString()} DZD</Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Urgent Alerts */}
      {reorderAlerts && reorderAlerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, ...animations.slideUp(0.3) }}>
          <strong>{reorderAlerts.length} product(s)</strong> need reordering soon!
        </Alert>
      )}

      <Card sx={animations.slideUp(0.4)}>
        <CardContent>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <WarningIcon fontSize="small" color={reorderAlerts?.length > 0 ? 'warning' : 'inherit'} />
                  <span>Reorder Alerts ({reorderAlerts?.length || 0})</span>
                </Stack>
              }
            />
            <Tab label="All Forecasts" />
          </Tabs>

          <TableToolbar
            title={tabValue === 0 ? 'Reorder Alerts' : 'Stock Forecasts'}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Search by product name..."
            filters={[]}
            activeFilters={{}}
            onFilterChange={() => {}}
            onClearFilters={() => {}}
            data={filteredData}
            columns={exportColumns}
            exportFileName={tabValue === 0 ? 'reorder-alerts' : 'stock-forecasts'}
          />

          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredData}
              columns={columns}
              rowCount={filteredData.length}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              autoHeight
              disableRowSelectionOnClick
              getRowId={(row) => row.productId}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
